import { useEffect } from 'react';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Hook para expirar automaticamente agendamentos pendentes após 24 horas
 * Se ultrapassar a data/hora do agendamento e ainda estiver pendente, marca como expirado
 * Só expira appointments da barbearia do owner logado
 */
export const useAppointmentExpiration = (currentUser) => {
  useEffect(() => {
    // Só executar se for owner
    if (!currentUser || currentUser.role !== 'owner') {
      return;
    }

    // Obter barbershopId (suporta formato antigo e novo)
    const barbershopId = currentUser.barbershopId || (currentUser.barbershops && currentUser.barbershops[0]);
    
    if (!barbershopId) {
      return;
    }

    const expirePendingAppointments = async () => {
      try {
        const now = new Date();
        const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        
        // Buscar agendamentos pendentes DA SUA BARBEARIA
        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef,
          where('barbershopId', '==', barbershopId),
          where('status', '==', 'pending')
        );

        const snapshot = await getDocs(q);
        
        const expiredAppointments = [];

        snapshot.docs.forEach(docSnapshot => {
          const data = docSnapshot.data();
          
          // Calcular data/hora do agendamento (parse local para evitar fuso UTC)
          const [hours, minutes] = data.time.split(':').map(Number);
          const [year, month, day] = data.date.split('-').map(Number);
          const appointmentDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
          
          // Se passou da data/hora do agendamento, expirar
          if (appointmentDate < now) {
            expiredAppointments.push({
              id: docSnapshot.id,
              data: data
            });
          }
          // Ou se foi criado há mais de 24h e ainda está pendente
          else if (data.createdAt) {
            const createdDate = new Date(data.createdAt);
            if (createdDate < twentyFourHoursAgo) {
              expiredAppointments.push({
                id: docSnapshot.id,
                data: data
              });
            }
          }
        });

        // Atualizar status para 'expired'
        const updatePromises = expiredAppointments.map(appointment =>
          updateDoc(doc(db, 'appointments', appointment.id), {
            status: 'expired',
            expiredAt: new Date().toISOString()
          })
        );

        await Promise.all(updatePromises);

        if (expiredAppointments.length > 0) {
          console.log(`✅ Auto-expiração: ${expiredAppointments.length} agendamento(s) pendente(s) expirado(s)`);
        }
      } catch (error) {
        console.error('❌ Erro na expiração de agendamentos:', error);
      }
    };

    // Executar ao montar (quando o owner abre o app)
    expirePendingAppointments();

    // Fallback: executar a cada 12 horas (economia de leituras Firebase)
    const interval = setInterval(expirePendingAppointments, 12 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentUser]);
};
